import { Box, Tooltip } from '@mui/material';
import { isPreviewMode } from '@/lib/previewMode';

// Build-Info aus vite.config.ts `define`. Fallback falls Konstante fehlt
// (z. B. lokale Dev-Builds vor Implementation).
const BUILD_COMMIT: string =
  typeof __BUILD_COMMIT__ === 'string' ? __BUILD_COMMIT__ : 'unknown';
const BUILD_TIME: string =
  typeof __BUILD_TIME__ === 'string' ? __BUILD_TIME__ : '';

function formatBuildTime(iso: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Höhe des Banners — wird von AppShell (im fixed-Mode) genutzt, um den Body
 * mit passendem `pt` nach unten zu schieben.
 */
export const PREVIEW_BANNER_HEIGHT = 28;

type Props = {
  /**
   * `fixed`: Banner klebt am Viewport-Top und scrollt mit (Standard für die
   *   App-Shell, weil der AppBar selbst ebenfalls fixed ist und der Banner
   *   sonst überschrieben würde).
   * `static` (Default): Banner steht im normalen Layout-Flow und scrollt
   *   beim Hochscrollen mit weg. Sinnvoll für Marketing-/Landing-Pages mit
   *   eigener sticky NavBar — dort soll der Banner beim Page-Load sofort
   *   sichtbar sein, muss aber nicht permanent am Viewport-Top kleben.
   */
  fixed?: boolean;
};

/**
 * Renders den orangen „Preview-Deployment"-Banner, wenn die App im
 * Preview-Modus läuft (siehe `src/lib/previewMode.ts`). In Production
 * rendert die Komponente nichts.
 */
export function PreviewBanner({ fixed = false }: Props) {
  if (!isPreviewMode()) return null;

  return (
    <Box
      role="status"
      sx={{
        ...(fixed
          ? {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: (t) => t.zIndex.tooltip + 1,
            }
          : { position: 'relative' }),
        height: `${PREVIEW_BANNER_HEIGHT}px`,
        bgcolor: '#f59e0b',
        color: '#1f2937',
        fontSize: '0.78rem',
        fontWeight: 600,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1,
        letterSpacing: 0.2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      🚧 Preview-Deployment — Daten landen in der echten DB, Stripe ist deaktiviert. Live:{' '}
      <strong>&nbsp;immokompass.app</strong>
      <Tooltip
        title={`Build: ${BUILD_COMMIT} · ${formatBuildTime(BUILD_TIME)}${BUILD_TIME ? ` UTC` : ''}`}
        arrow
      >
        <Box
          component="span"
          sx={{
            ml: 1.5,
            px: 0.75,
            py: 0.1,
            borderRadius: 0.5,
            bgcolor: 'rgba(0,0,0,0.15)',
            fontWeight: 500,
            fontSize: '0.72rem',
            fontFamily: 'monospace',
            cursor: 'help',
          }}
        >
          {BUILD_COMMIT}
        </Box>
      </Tooltip>
    </Box>
  );
}
